import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, PlayCircle, Clock, User, ImageOff,
  CheckCircle, Loader, X, Award, BookMarked, Radio,
} from "lucide-react";
import { lmsGetCourses, lmsGetStudentEnrolledCourses, lmsGetLiveClasses } from "../api/lmsApi";
import { JoinModal } from "./LiveClasses";
import "../lms.css";

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const studentId = ud.student_id || ud.id || null;
    const schoolId = ud.school_id || ud.admin_id || ud.parent_id || null;
    const classId = ud.class_id || null;
    return { studentId, schoolId, classId, ...ud };
  } catch {
    return { studentId: null, schoolId: null, classId: null };
  }
};

export default function StudentEnrolledCourses() {
  const navigate = useNavigate();
  const { studentId, schoolId, classId } = getStudentData();

  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCourse, setActiveCourse] = useState(null);
  const [joinLiveItem, setJoinLiveItem] = useState(null);

  useEffect(() => {
    fetchEnrolledCourses();
    fetchLiveClasses();
  }, [studentId, schoolId]);

  const fetchEnrolledCourses = async () => {
    setLoading(true);
    try {
      if (studentId) {
        const res = await lmsGetStudentEnrolledCourses(studentId, schoolId);
        const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setEnrolledCourses(data);
      } else {
        const res = await lmsGetCourses({ school_id: schoolId });
        const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setEnrolledCourses(data);
      }
    } catch (error) {
      console.error("Error fetching enrolled courses:", error);
      setEnrolledCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveClasses = async () => {
    const extract = (res) => res.data?.data || (Array.isArray(res.data) ? res.data : []);
    try {
      const res = await lmsGetLiveClasses(schoolId ? { school_id: schoolId } : {});
      let data = extract(res);
      if (!data.length && schoolId) {
        const res2 = await lmsGetLiveClasses({});
        data = extract(res2);
      }
      setLiveClasses(data);
    } catch {
      setLiveClasses([]);
    }
  };

  const getActiveLiveClass = (course) => {
    const now = new Date();
    const isActive = (lc) => {
      if (lc.status === "ended") return false;
      if (lc.status === "live") return true;
      if (!lc.scheduled_at) return false;
      const start = new Date(lc.scheduled_at);
      const end = new Date(start.getTime() + (lc.duration_minutes || 60) * 60000);
      return now >= start && now <= end;
    };
    const cid = course.class_id || classId;
    const sid = course.subject_id;
    // 1. Exact class + subject match
    if (cid && sid) {
      const byExact = liveClasses.find(
        (lc) => isActive(lc) &&
          String(lc.class_id) === String(cid) &&
          String(lc.subject_id) === String(sid)
      );
      if (byExact) return byExact;
    }
    // 2. Same class, live class has no subject restriction
    if (cid) {
      const byClass = liveClasses.find(
        (lc) => isActive(lc) && String(lc.class_id) === String(cid) && !lc.subject_id
      );
      if (byClass) return byClass;
    }
    return null;
  };

  return (
    <div>
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Loader size={32} className="spinner" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "#64748b", fontSize: "15px" }}>Loading your enrolled courses...</p>
        </div>
      ) : enrolledCourses.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <BookMarked size={48} color="#94a3b8" style={{ margin: "0 auto 12px" }} />
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#334155" }}>
            No enrolled courses yet
          </h3>
          <p style={{ fontSize: "14px", color: "#94a3b8", marginTop: "4px" }}>
            Switch to the "Available Courses" tab to enroll in your first course.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "24px",
          }}
        >
          {enrolledCourses.map((course, index) => {
            // Simulated learning progress
            const progress = (index * 25 + 35) % 100;
            const cName = course.course_name || course.name;
            const thumb = course.course_thumbnail || course.thumbnail_url;
            const activeLive = getActiveLiveClass(course);

            return (
              <div
                key={course.id || course.enrollment_id || index}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    height: "160px",
                    background: "#1c498d",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={cName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <ImageOff size={32} color="#ffffff" />
                  )}
                  <span
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      background: "rgba(15, 23, 42, 0.75)",
                      color: "#ffffff",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <CheckCircle size={13} color="#22c55e" />
                    Enrolled
                  </span>
                </div>

                {/* Content */}
                <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                  <h3
                    style={{
                      fontSize: "17px",
                      fontWeight: "600",
                      color: "#0f172a",
                      marginBottom: "6px",
                      lineHeight: "1.4",
                    }}
                  >
                    {cName}
                  </h3>
                  {course.instructor_name && (
                    <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                      Instructor: {course.instructor_name}
                    </p>
                  )}

                  {/* Learning Progress Bar */}
                  <div style={{ marginTop: "auto", marginBottom: "18px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#475569",
                        marginBottom: "6px",
                      }}
                    >
                      <span>Course Progress</span>
                      <span>{progress}%</span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        background: "#e2e8f0",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          background: progress >= 100 ? "#22c55e" : "#1c498d",
                          borderRadius: "4px",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Footer Action */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingTop: "14px",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#64748b", display: "flex", alignItems: "center", gap: "4px" }}>
                      <Clock size={13} /> Active
                    </span>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      {activeLive && (
                        <button
                          type="button"
                          onClick={() => setJoinLiveItem(activeLive)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 14px",
                            borderRadius: "8px",
                            background: "#dc2626",
                            color: "#ffffff",
                            border: "none",
                            fontWeight: "600",
                            fontSize: "13px",
                            cursor: "pointer",
                            animation: "pulse 2s infinite",
                          }}
                        >
                          <Radio size={14} />
                          Join Live
                        </button>
                      )}
                      {(() => {
                        const isExpired = course.end_date && new Date(course.end_date) < new Date();
                        return (
                          <button
                            type="button"
                            disabled={isExpired}
                            onClick={() => {
                              if (isExpired) return;
                              const cid = course.course_id || course.id;
                              if (cid) navigate(`/student-lms/courses/${cid}`);
                            }}
                            title={isExpired ? "This course has ended" : ""}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "6px",
                              padding: "8px 16px", borderRadius: "8px",
                              background: isExpired ? "#e2e8f0" : "#1c498d",
                              color: isExpired ? "#94a3b8" : "#ffffff",
                              border: "none", fontWeight: "600", fontSize: "13px",
                              cursor: isExpired ? "not-allowed" : "pointer",
                              opacity: isExpired ? 0.7 : 1,
                            }}
                          >
                            <PlayCircle size={15} />
                            {isExpired ? "Course Ended" : "Continue Learning"}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Join Live Class Modal */}
      {joinLiveItem && (
        <JoinModal liveClass={joinLiveItem} onClose={() => setJoinLiveItem(null)} studentMode />
      )}

      {/* Learning Classroom Modal */}
      {activeCourse && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setActiveCourse(null)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "600px",
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px", background: "#1c498d", color: "#ffffff", position: "relative" }}>
              <button
                onClick={() => setActiveCourse(null)}
                style={{
                  position: "absolute",
                  top: "18px",
                  right: "18px",
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={18} />
              </button>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
                {activeCourse.name}
              </h2>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: "14px" }}>
                Student Learning Session
              </p>
            </div>

            <div style={{ padding: "24px" }}>
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ fontSize: "15px", fontWeight: "600", color: "#0f172a", marginBottom: "8px" }}>
                  Course Syllabus & Study Materials
                </h4>
                <p style={{ fontSize: "14px", color: "#475569", lineHeight: "1.6" }}>
                  {activeCourse.description || "Welcome to your digital classroom session. Access chapters, video lectures, and study documents below."}
                </p>
              </div>

              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "16px",
                  background: "#f8fafc",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <Award size={20} color="#1c498d" />
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#0f172a" }}>
                    Module 1: Fundamental Concepts & Orientation
                  </span>
                </div>
                <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                  Includes video tutorials, reading materials, and chapter quizzes.
                </p>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setActiveCourse(null)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#1c498d",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
